import { Dialog, DialogContent } from '@mui/material';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';

import { useUserStore } from '@/configs/store';
import { useYaMetrika } from '@/entities/yaMetrika';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { AnalyticsEvent } from '@/types/yametrika';
import { YandexUser } from '@/types/yandex';
import { extractTaskKeyFromUrl } from '@/utils/extractTaskKeyFromUrl';
import { isMac } from '@/utils/isMac';

import { ConfirmDialog } from '../confirm';

import { useInternalCompanion, useTimeLogSubmit, useWarningDialog } from './hooks';
import { DataValues, FormFields, FormValues } from './types';
import {
  CommentSection,
  DateEntriesSection,
  DialogFooter,
  DialogHeader,
  TaskSelector,
  UsersSection,
} from './ui';
import { areAllEntriesValid } from './utils';

import s from './TimeLogForm.module.scss';

type TimeLogProps = {
  open: boolean;
  formValues: FormValues;
  onClose: () => void;
  onSubmit: (data: DataValues) => Promise<void>;
  isEdit?: boolean;
  isCopy?: boolean;
  hasPreselectedDate?: boolean;
  isTaskSelected?: boolean;
  isSingleEntry?: boolean;
  companionPanel?: React.ReactNode;
  onUsersChange?: (users: YandexUser[]) => void;
  onTaskKeyChange?: (taskKey: string | null) => void;
  autoShowCompanion?: boolean;
  onDelete?: () => void;
};

const TimeLogForm: React.FC<TimeLogProps> = observer(
  ({
    open,
    onClose,
    formValues,
    onSubmit,
    isEdit,
    isCopy,
    hasPreselectedDate,
    isTaskSelected,
    isSingleEntry,
    companionPanel,
    onUsersChange,
    onTaskKeyChange,
    autoShowCompanion,
    onDelete,
  }) => {
    const { setSuggestQuery, suggestions } = useTimeLogStore();
    const { user } = useUserStore();
    const { sendEvent } = useYaMetrika();

    const {
      control,
      handleSubmit,
      watch,
      reset,
      setValue,
      formState: { errors },
    } = useForm<FormValues>({
      defaultValues: formValues,
    });

    const watchedTaskLabel = watch(FormFields.taskLabel);
    const watchedTaskKey = watch(FormFields.taskKey);
    const watchedDateEntries = watch(FormFields.dateEntries);

    const { fields, append, remove } = useFieldArray({
      control,
      name: 'dateEntries',
    });

    const handleSuccess = () => {
      reset();
      onClose();
    };

    const { submit, isSubmitting } = useTimeLogSubmit({
      onSubmit,
      onSuccess: handleSuccess,
      userId: user?.id,
    });

    const warningDialog = useWarningDialog();

    useEffect(() => {
      if (isEdit || isCopy) return;

      const trimmed = watchedTaskLabel.trim();

      if (trimmed) {
        setSuggestQuery(extractTaskKeyFromUrl(trimmed));
      } else {
        setSuggestQuery('');
      }
    }, [watchedTaskLabel, setSuggestQuery, isEdit, isCopy]);

    useEffect(() => {
      if (open) {
        reset(formValues);
      }
    }, [open, formValues, reset]);

    const watchedUsers = watch(FormFields.users);

    useEffect(() => {
      onUsersChange?.(watchedUsers || []);
    }, [watchedUsers, onUsersChange]);

    useEffect(() => {
      onTaskKeyChange?.(watchedTaskKey || null);
    }, [watchedTaskKey, onTaskKeyChange]);

    const {
      showCompanion,
      toggleCompanion,
      panel: companionPanelContent,
    } = useInternalCompanion({
      companionPanel,
      startDate: formValues.dateEntries[0]?.startDate ?? '',
      user,
      watchedTaskKey,
      watchedUsers,
      autoShowCompanion,
    });

    const handleDialogClose = () => {
      reset();
      onClose();
    };

    const isTaskValid = !!watchedTaskKey;

    const isDurationValid =
      (watchedDateEntries?.length ?? 0) > 0 && areAllEntriesValid(watchedDateEntries ?? []);

    const isFormValid = isTaskValid && isDurationValid;

    const doSubmit = (data: FormValues) => {
      submit(data);
    };

    const handleFormSubmit = (data: FormValues) => {
      warningDialog.checkAndSubmit(data, doSubmit);
    };

    const handleConfirmWarning = () => {
      warningDialog.confirm(doSubmit);
    };

    useHotkeys(
      isMac ? 'meta+enter' : 'alt+enter',
      (e) => {
        e.preventDefault();

        sendEvent(AnalyticsEvent.useHotkeys);
        handleSubmit(handleFormSubmit)();
      },
      {
        enableOnFormTags: ['INPUT', 'SELECT', 'TEXTAREA'],
      },
    );

    return (
      <>
        <Dialog
          className={s.timelog_form}
          open={open}
          onClose={handleDialogClose}
          fullWidth
          sx={{
            '.MuiPaper-root': {
              borderRadius: '8px',
              overflow: showCompanion ? 'visible' : undefined,
              overflowX: showCompanion ? undefined : 'hidden',
            },
            '.MuiDialog-container': {
              transition: 'padding-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              paddingRight: showCompanion ? '316px' : undefined,
            },
          }}
        >
          <DialogHeader isEdit={isEdit} isCopy={isCopy} onDelete={onDelete} />
          <DialogContent sx={{ display: 'flex', paddingTop: 0, flexDirection: 'column', gap: 2 }}>
            <TaskSelector
              control={control}
              watchedTaskLabel={watchedTaskLabel}
              isTaskValid={isTaskValid}
              errors={errors}
              suggestions={suggestions}
              setValue={setValue}
              taskKey={formValues.taskKey}
              isEdit={isEdit}
              isCopy={isCopy}
              isTaskSelected={isTaskSelected}
            />

            <DateEntriesSection
              control={control}
              fields={fields}
              watchedDateEntries={watchedDateEntries}
              errors={errors}
              isEdit={isEdit}
              isCopy={isCopy}
              remove={remove}
              append={append}
              setValue={setValue}
              hasPreselectedDate={hasPreselectedDate}
              isSingleEntry={isSingleEntry}
              isTaskSelected={isTaskSelected}
            />

            {!isEdit && (
              <UsersSection
                control={control}
                showCompanion={showCompanion}
                onToggleCompanion={toggleCompanion}
              />
            )}

            <CommentSection control={control} />
          </DialogContent>
          <DialogFooter
            isEdit={isEdit}
            isFormValid={isFormValid}
            isSubmitting={isSubmitting}
            onCancel={handleDialogClose}
            onSubmit={handleSubmit(handleFormSubmit)}
          />
          <div
            className={clsx(
              s.timelog_form__companion,
              !showCompanion && s.timelog_form__companion_hidden,
            )}
          >
            {companionPanelContent}
          </div>
        </Dialog>
        <ConfirmDialog
          open={warningDialog.isOpen}
          onClose={warningDialog.close}
          onConfirm={handleSubmit(handleConfirmWarning)}
        />
      </>
    );
  },
);

export { TimeLogForm };
